import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from './SafeIcon';
import QuantumLogo from './QuantumLogo';
import * as FiIcons from 'react-icons/fi';
import { FaTwitter, FaTelegram, FaFacebook, FaTiktok, FaSnapchatGhost, FaLinkedin } from 'react-icons/fa';

const { FiZap, FiShield, FiActivity, FiLock } = FiIcons;

const Footer = () => {
  const handleAnchorClick = (e, anchor) => {
    e.preventDefault();
    const element = document.getElementById(anchor);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const socialLinks = [
    { icon: FaTwitter, href: 'https://twitter.com/quantumai_ft', label: 'Twitter' },
    { icon: FaTelegram, href: 'https://t.me/quantumaifintech', label: 'Telegram' },
    { icon: FaFacebook, href: 'https://facebook.com/quantumaifintech', label: 'Facebook' },
    { icon: FaTiktok, href: 'https://tiktok.com/@quantumaifintech', label: 'TikTok' },
    { icon: FaSnapchatGhost, href: 'https://snapchat.com/add/quantumaifintech', label: 'Snapchat' },
    { icon: FaLinkedin, href: 'https://linkedin.com/company/quantum-ai-fintech', label: 'LinkedIn' },
  ];

  const quickLinks = [
    { name: 'What', anchor: 'what' },
    { name: 'Partners', anchor: 'partners' },
    { name: 'Why', anchor: 'why' },
    { name: 'How', anchor: 'how' },
    { name: 'Products', anchor: 'features' },
    { name: 'Pricing', anchor: 'pricing' },
    { name: 'Testimonials', anchor: 'testimonials' },
    { name: 'FAQ', anchor: 'faq' },
    { name: 'Contact', anchor: 'contact' },
  ];

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    if (email) {
      alert(`Thank you for subscribing with ${email}!`);
      e.target.reset();
    }
  };

  return (
    <footer className="relative bg-gradient-to-b from-gray-900 to-black border-t border-cyan-500/20">
      {/* Top Gradient Line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6 hover:opacity-90 transition-opacity">
              <QuantumLogo size="default" />
            </Link>
            <p className="text-gray-400 leading-relaxed mb-4 max-w-md font-medium">
              Enterprise-Grade AI Solutions for Financial Institutions and Trading Operations
            </p>
            <p className="text-gray-500 leading-relaxed max-w-md text-sm">
              Designed to empower banks, hedge funds, asset managers, and investment teams with AI-driven risk management, capital preservation, and multi-asset execution intelligence.
            </p>

            {/* Legal Disclaimer */}
            <div className="mt-6 pt-6 border-t border-white/5 max-w-md">
              <p className="text-gray-500 text-xs leading-relaxed mb-3">
                <span className="font-semibold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">No Financial Licensing:</span>{' '}
                Quantum AI FinTech Pte. Ltd., Quantum AI FinTech Corporation SA, and all affiliated brands (collectively, the "Service"), are not licensed, registered, or authorized to provide financial, investment, or trading advice in any jurisdiction. The Service does not offer brokerage, investment advisory services, portfolio management, or any regulated financial activity.
              </p>
              <p className="text-gray-500 text-xs leading-relaxed">
                <span className="font-semibold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Trading and Investment Risks:</span>{' '}
                Trading and investing in financial instruments—including stocks, options, futures, foreign exchange, derivatives, and cryptocurrencies—involves substantial risk of loss. Users may lose part or all of their invested capital. Past performance, historical signals, AI model behavior, or simulated outcomes do not guarantee future results. Users must carefully assess their personal financial circumstances, experience level, and risk tolerance before using the Service.
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
              <span className="w-1 h-6 bg-gradient-to-b from-purple-600 to-cyan-600 rounded-full"></span>
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Quick Links</span>
            </h3>
            <ul className="grid grid-cols-2 gap-x-8 gap-y-3">
              {quickLinks.map((link) => (
                <li key={link.anchor || link.path}>
                  {link.anchor ? (
                    <button
                      onClick={(e) => handleAnchorClick(e, link.anchor)}
                      className="text-gray-400 hover:bg-gradient-to-r hover:from-purple-400 hover:to-cyan-400 hover:bg-clip-text hover:text-transparent transition-all flex items-center gap-2 group w-full text-left"
                    >
                      <span className="w-0 h-px bg-gradient-to-r from-purple-400 to-cyan-400 group-hover:w-4 transition-all duration-300"></span>
                      {link.name}
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      className="text-gray-400 hover:bg-gradient-to-r hover:from-purple-400 hover:to-cyan-400 hover:bg-clip-text hover:text-transparent transition-all flex items-center gap-2 group"
                    >
                      <span className="w-0 h-px bg-gradient-to-r from-purple-400 to-cyan-400 group-hover:w-4 transition-all duration-300"></span>
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Connect & Newsletter */}
          <div>
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <span className="w-1 h-6 bg-gradient-to-b from-purple-600 to-cyan-600 rounded-full"></span>
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Connect</span>
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              Stay close to product updates, new models, and institutional research.
            </p>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative p-4 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 rounded-lg transition-all hover:scale-110 shadow-lg shadow-purple-500/20 flex items-center justify-center"
                  aria-label={social.label}
                >
                  <social.icon className="text-2xl text-cyan-400" />
                </a>
              ))}
            </div>

            {/* Newsletter */}
            <div>
              <h4 className="text-sm font-semibold mb-2 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Stay Updated</h4>
              <p className="text-gray-500 text-xs mb-3">
                Get early access to releases, trading tools, and institutional risk updates.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-4 py-2 bg-gray-800/50 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
                />
                <button 
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-lg font-semibold text-sm text-cyan-400 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 hover:scale-105 transition-all"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-400 text-center md:text-left">
              © 2025 <span className="font-semibold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Quantum AI Fintech Corporation SA</span>. All rights reserved.
            </p>
            
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/risk-disclaimer" className="hover:bg-gradient-to-r hover:from-purple-400 hover:to-cyan-400 hover:bg-clip-text hover:text-transparent transition-all">
                Risk Disclaimer
              </Link>
              <Link to="/privacy" className="hover:bg-gradient-to-r hover:from-purple-400 hover:to-cyan-400 hover:bg-clip-text hover:text-transparent transition-all">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:bg-gradient-to-r hover:from-purple-400 hover:to-cyan-400 hover:bg-clip-text hover:text-transparent transition-all">
                Terms of Service
              </Link>
              <Link to="/cookies" className="hover:bg-gradient-to-r hover:from-purple-400 hover:to-cyan-400 hover:bg-clip-text hover:text-transparent transition-all">
                Cookies
              </Link>
              <Link to="/legal/defensive-publications" className="hover:bg-gradient-to-r hover:from-purple-400 hover:to-cyan-400 hover:bg-clip-text hover:text-transparent transition-all">
                Defensive Publications
              </Link>
            </div>
          </div>

          {/* System Status & Trust Indicators */}
          <div className="mt-8">
            <p className="text-center text-xs text-gray-600 mb-4">System Status & Trust Indicators</p>
            <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></div>
                <span>99.9% Uptime</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiZap} className="text-cyan-400" />
                <span>AI-Powered Analytics</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiLock} className="text-cyan-400" />
                <span>Bank-Level Security</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiActivity} className="text-cyan-400" />
                <span>Real-Time Market Engine</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiShield} className="text-cyan-400" />
                <span>Zero-Data Sharing Policy</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
